import type {
  AdminAppUserCreate,
  AdminAppUserRow,
  AdminAppUsersResponse,
  AdminFeatureFlagRow,
  AdminFeatureFlagsResponse,
  AdminJunctionPatch,
  AdminJunctionWrite,
  AdminNetworkMutationResponse,
  AdminNetworkRow,
  AdminNetworksResponse,
  AdminPdfDatasetRow,
  AdminPdfDatasetsResponse,
  AdminPdfDocumentKind,
  AdminPdfImportResult,
  AdminRegulationRoutePatch,
  AdminRegulationRouteWrite,
  AdminSessionResponse,
  AdminSourceRow,
  AdminSourcesResponse,
  AdminTurnbackStop,
  AdminTurnbackStopsResponse,
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantConversation,
  AssistantConversationDetailResponse,
  AuditLogEntry,
  AuditLogResponse,
  Junction,
  OpaqueId,
  RegRoute,
} from "../contract/types";
import { HttpClient, type HttpClientOptions } from "./http";

export interface PdfImportInput {
  file: File;
  documentKind: AdminPdfDocumentKind;
  /** kebab-case, ex. « metro-rouge », « services-bus ». */
  sourceLabel: string;
  /** Activer le jeu importé dans la foulée (rollback de l'ancien du même `sourceLabel`). */
  activate?: boolean;
}

/**
 * Client de la surface `/admin/**` (contrat §8, v1.11). Régime d'identité
 * distinct du poste : le `HttpClient` fourni ici résout un **jeton admin**
 * (`adminSessionStore`). Toutes les routes sauf `login` exigent ce jeton ; un
 * jeton de poste y répond `401`.
 *
 * Lot 1 : bascules fonctionnelles, sources temps réel, documents PDF, réseaux,
 * journal d'audit. La curation (carrefours / itinéraires / retournement) est le
 * lot 2.
 */
export class AdminApi {
  readonly http: HttpClient;

  constructor(options: HttpClientOptions | HttpClient) {
    this.http = options instanceof HttpClient ? options : new HttpClient(options);
  }

  private net(networkId: OpaqueId): string {
    return `/admin/networks/${encodeURIComponent(networkId)}`;
  }

  // ── Authentification ─────────────────────────────────────────────────────
  login(username: string, secret: string, signal?: AbortSignal): Promise<AdminSessionResponse> {
    return this.http.post<AdminSessionResponse>("/auth/session", {
      body: { kind: "admin", username, secret },
      ...(signal ? { signal } : {}),
    });
  }

  // ── Réseaux (non scopé — B7.5) ───────────────────────────────────────────
  async listNetworks(signal?: AbortSignal): Promise<AdminNetworkRow[]> {
    const r = await this.http.get<AdminNetworksResponse>("/admin/networks", signal ? { signal } : {});
    return r.networks;
  }

  // ── Accès à la porte d'application (non scopé — v1.40) ────────────────────
  // Comptes applicatifs seulement (§8bis) ; les comptes admin restent
  // provisionnés hors-ligne (`bin/create-admin.ts`), jamais exposés ici.
  async listAppUsers(signal?: AbortSignal): Promise<AdminAppUserRow[]> {
    const r = await this.http.get<AdminAppUsersResponse>("/admin/app-users", signal ? { signal } : {});
    return r.users;
  }

  createAppUser(body: AdminAppUserCreate, signal?: AbortSignal): Promise<AdminAppUserRow> {
    return this.http.post<AdminAppUserRow>("/admin/app-users", { body, ...(signal ? { signal } : {}) });
  }

  /** Définitif — coupe la session en cours de ce compte. */
  deleteAppUser(username: string, signal?: AbortSignal): Promise<unknown> {
    return this.http.del(`/admin/app-users/${encodeURIComponent(username)}`, signal ? { signal } : {});
  }

  /** Active / retire un réseau à chaud (sans réécrire sa config). */
  setNetworkEnabled(
    networkId: OpaqueId,
    enabled: boolean,
    signal?: AbortSignal,
  ): Promise<AdminNetworkMutationResponse> {
    return this.http.patch<AdminNetworkMutationResponse>(`/admin/networks/${encodeURIComponent(networkId)}`, {
      body: { enabled },
      ...(signal ? { signal } : {}),
    });
  }

  // ── Bascules fonctionnelles ──────────────────────────────────────────────
  async getFeatureFlags(networkId: OpaqueId, signal?: AbortSignal): Promise<AdminFeatureFlagRow[]> {
    const r = await this.http.get<AdminFeatureFlagsResponse>(
      `${this.net(networkId)}/feature-flags`,
      signal ? { signal } : {},
    );
    return r.flags;
  }

  /** Pose une surcharge admin — effet immédiat sur `GET /config`. */
  setFeatureFlagOverride(
    networkId: OpaqueId,
    key: string,
    enabled: boolean,
    signal?: AbortSignal,
  ): Promise<unknown> {
    return this.http.put(`${this.net(networkId)}/feature-flags/${encodeURIComponent(key)}`, {
      body: { enabled },
      ...(signal ? { signal } : {}),
    });
  }

  /** Retire la surcharge — retour au défaut du fichier de config. */
  clearFeatureFlagOverride(networkId: OpaqueId, key: string, signal?: AbortSignal): Promise<unknown> {
    return this.http.del(`${this.net(networkId)}/feature-flags/${encodeURIComponent(key)}`, signal ? { signal } : {});
  }

  // ── Sources temps réel ───────────────────────────────────────────────────
  async getSources(networkId: OpaqueId, signal?: AbortSignal): Promise<AdminSourceRow[]> {
    const r = await this.http.get<AdminSourcesResponse>(`${this.net(networkId)}/sources`, signal ? { signal } : {});
    return r.sources;
  }

  setSourceEnabledOverride(
    networkId: OpaqueId,
    sourceId: string,
    enabled: boolean,
    signal?: AbortSignal,
  ): Promise<AdminSourceRow> {
    return this.http.put<AdminSourceRow>(
      `${this.net(networkId)}/sources/${encodeURIComponent(sourceId)}/enabled`,
      { body: { enabled }, ...(signal ? { signal } : {}) },
    );
  }

  clearSourceEnabledOverride(networkId: OpaqueId, sourceId: string, signal?: AbortSignal): Promise<unknown> {
    return this.http.del(
      `${this.net(networkId)}/sources/${encodeURIComponent(sourceId)}/enabled`,
      signal ? { signal } : {},
    );
  }

  /** Signal à écriture seule : repart d'un état de santé neuf au prochain cycle. */
  resyncSource(
    networkId: OpaqueId,
    sourceId: string,
    signal?: AbortSignal,
  ): Promise<{ sourceId: string; resyncRequestedAt: string }> {
    return this.http.post(`${this.net(networkId)}/sources/${encodeURIComponent(sourceId)}/resync`, signal ? { signal } : {});
  }

  // ── Documents d'exploitation PDF ─────────────────────────────────────────
  async getPdfDatasets(networkId: OpaqueId, signal?: AbortSignal): Promise<AdminPdfDatasetRow[]> {
    const r = await this.http.get<AdminPdfDatasetsResponse>(
      `${this.net(networkId)}/pdf-datasets`,
      signal ? { signal } : {},
    );
    return r.datasets;
  }

  importPdfDataset(networkId: OpaqueId, input: PdfImportInput, signal?: AbortSignal): Promise<AdminPdfImportResult> {
    const form = new FormData();
    form.set("file", input.file);
    form.set("documentKind", input.documentKind);
    form.set("sourceLabel", input.sourceLabel);
    if (input.activate) form.set("activate", "true");
    return this.http.post<AdminPdfImportResult>(`${this.net(networkId)}/pdf-datasets`, {
      body: form,
      ...(signal ? { signal } : {}),
    });
  }

  activatePdfDataset(networkId: OpaqueId, datasetId: string, signal?: AbortSignal): Promise<unknown> {
    return this.http.post(
      `${this.net(networkId)}/pdf-datasets/${encodeURIComponent(datasetId)}/activate`,
      signal ? { signal } : {},
    );
  }

  // ── Journal d'audit ──────────────────────────────────────────────────────
  async getAuditLog(networkId: OpaqueId, opts: { limit?: number } = {}, signal?: AbortSignal): Promise<AuditLogEntry[]> {
    const r = await this.http.get<AuditLogResponse>(`${this.net(networkId)}/audit-log`, {
      query: { limit: opts.limit },
      ...(signal ? { signal } : {}),
    });
    return r.entries;
  }

  // ── Assistant IA (v1.56) — consultatif, aucun outil mutant dans sa table de
  // dispatch côté serveur (CDC « conseille, n'agit pas »). Réservé aux comptes
  // listés dans `config.assistant.allowedAdminUsernames` — le front ne teste
  // jamais un nom de compte, seulement `AppMeResponse.assistantEnabled`. ─────
  async getAssistantConversations(networkId: OpaqueId, signal?: AbortSignal): Promise<AssistantConversation[]> {
    const r = await this.http.get<{ conversations: AssistantConversation[] }>(
      `${this.net(networkId)}/assistant/conversations`,
      signal ? { signal } : {},
    );
    return r.conversations;
  }

  getAssistantConversation(
    networkId: OpaqueId,
    conversationId: OpaqueId,
    signal?: AbortSignal,
  ): Promise<AssistantConversationDetailResponse> {
    return this.http.get<AssistantConversationDetailResponse>(
      `${this.net(networkId)}/assistant/conversations/${encodeURIComponent(conversationId)}`,
      signal ? { signal } : {},
    );
  }

  /** Délai étendu (v1.56) : un tour peut enchaîner plusieurs outils coûteux (OSRM). */
  postAssistantChat(networkId: OpaqueId, body: AssistantChatRequest, signal?: AbortSignal): Promise<AssistantChatResponse> {
    return this.http.post<AssistantChatResponse>(`${this.net(networkId)}/assistant/chat`, {
      body,
      timeoutMs: 60_000,
      ...(signal ? { signal } : {}),
    });
  }

  // ══ Lot 2 — curation des données gérées ═════════════════════════════════

  // ── Carrefours (le `status` reste hors admin, il se pose depuis la console) ─
  async listJunctions(networkId: OpaqueId, opts: { q?: string } = {}, signal?: AbortSignal): Promise<Junction[]> {
    const r = await this.http.get<{ junctions: Junction[] }>(`${this.net(networkId)}/junctions`, {
      query: { q: opts.q },
      ...(signal ? { signal } : {}),
    });
    return r.junctions;
  }

  createJunction(networkId: OpaqueId, body: AdminJunctionWrite, signal?: AbortSignal): Promise<Junction> {
    return this.http.post<Junction>(`${this.net(networkId)}/junctions`, { body, ...(signal ? { signal } : {}) });
  }

  patchJunction(
    networkId: OpaqueId,
    junctionId: OpaqueId,
    body: AdminJunctionPatch,
    signal?: AbortSignal,
  ): Promise<Junction> {
    return this.http.patch<Junction>(`${this.net(networkId)}/junctions/${encodeURIComponent(junctionId)}`, {
      body,
      ...(signal ? { signal } : {}),
    });
  }

  deleteJunction(networkId: OpaqueId, junctionId: OpaqueId, signal?: AbortSignal): Promise<unknown> {
    return this.http.del(`${this.net(networkId)}/junctions/${encodeURIComponent(junctionId)}`, signal ? { signal } : {});
  }

  // ── Itinéraires de régulation (GET admin = toutes les entrées, actives ou non) ─
  async listRegRoutes(networkId: OpaqueId, opts: { lineId?: OpaqueId } = {}, signal?: AbortSignal): Promise<RegRoute[]> {
    const r = await this.http.get<{ regRoutes: RegRoute[] }>(`${this.net(networkId)}/reg-routes`, {
      query: { line: opts.lineId },
      ...(signal ? { signal } : {}),
    });
    return r.regRoutes;
  }

  createRegRoute(networkId: OpaqueId, body: AdminRegulationRouteWrite, signal?: AbortSignal): Promise<RegRoute> {
    return this.http.post<RegRoute>(`${this.net(networkId)}/reg-routes`, { body, ...(signal ? { signal } : {}) });
  }

  patchRegRoute(
    networkId: OpaqueId,
    routeId: OpaqueId,
    body: AdminRegulationRoutePatch,
    signal?: AbortSignal,
  ): Promise<RegRoute> {
    return this.http.patch<RegRoute>(`${this.net(networkId)}/reg-routes/${encodeURIComponent(routeId)}`, {
      body,
      ...(signal ? { signal } : {}),
    });
  }

  deleteRegRoute(networkId: OpaqueId, routeId: OpaqueId, signal?: AbortSignal): Promise<unknown> {
    return this.http.del(`${this.net(networkId)}/reg-routes/${encodeURIComponent(routeId)}`, signal ? { signal } : {});
  }

  // ── Points de retournement (curation d'une ligne) ─────────────────────────
  async listTurnbackStops(networkId: OpaqueId, lineId: OpaqueId, signal?: AbortSignal): Promise<AdminTurnbackStop[]> {
    const r = await this.http.get<AdminTurnbackStopsResponse>(
      `${this.net(networkId)}/lines/${encodeURIComponent(lineId)}/turnback-stops`,
      signal ? { signal } : {},
    );
    return r.stops;
  }

  setTurnbackStop(
    networkId: OpaqueId,
    lineId: OpaqueId,
    stopId: OpaqueId,
    eligible: boolean,
    signal?: AbortSignal,
  ): Promise<AdminTurnbackStop> {
    return this.http.put<AdminTurnbackStop>(
      `${this.net(networkId)}/lines/${encodeURIComponent(lineId)}/turnback-stops/${encodeURIComponent(stopId)}`,
      { body: { eligible }, ...(signal ? { signal } : {}) },
    );
  }

  /** Retire complètement la curation (distinct de `eligible: false`). */
  clearTurnbackStop(
    networkId: OpaqueId,
    lineId: OpaqueId,
    stopId: OpaqueId,
    signal?: AbortSignal,
  ): Promise<unknown> {
    return this.http.del(
      `${this.net(networkId)}/lines/${encodeURIComponent(lineId)}/turnback-stops/${encodeURIComponent(stopId)}`,
      signal ? { signal } : {},
    );
  }

  // ── Config réseau complète (avancé — validée serveur) ─────────────────────
  putNetworkConfig(
    networkId: OpaqueId,
    config: unknown,
    signal?: AbortSignal,
  ): Promise<AdminNetworkMutationResponse> {
    return this.http.put<AdminNetworkMutationResponse>(`/admin/networks/${encodeURIComponent(networkId)}`, {
      body: config,
      ...(signal ? { signal } : {}),
    });
  }
}
